import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Select,
  Input,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Tooltip,
  Tabs,
  Space
} from 'antd';
import Chart from '@components/charts/Chart';
import {
  FilterOutlined,
  SearchOutlined,
  SyncOutlined,
  ClearOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { isNumber } from 'lodash';
import { showError } from '@components/notification';
import {
  apiGetNightMinConsumptionTracking,
  apiClearNightMinConsumptionCache,
  apiExportNightMinConsumptionTracking
} from '@services/ManageCustomers/CustomerServices';
import { apiGetFavoriteFilters } from '@services/ManageCustomers/FavoriteFilterServices';
import { apiSyncTimeData } from '@services/ManageDevices/DeviceServices';
import FilterAdvanceDrawer from '@components/FilterAdvance';
import Loading from '@components/Loading';
import { usePermission } from '@contexts/Auth/UsePermission';
import { formatCurrency } from '@utils/utils';

/**
 * Định dạng giá trị lít sang m3 với 3 chữ số thập phân.
 * Trả về '-' nếu giá trị không hợp lệ.
 */
function formatCubicMeters(val) {
  if (!isNumber(val) || !isFinite(val)) return '-';
  return formatCurrency(val / 1000, 3);
}

function toCubicMeters(val) {
  if (!isNumber(val) || !isFinite(val)) return null;
  return val / 1000;
}

/**
 * Tạo danh sách 30 ngày gần nhất (từ hôm nay trừ 29 ngày → hôm nay).
 * @returns {string[]} mảng chuỗi YYYY-MM-DD
 */
function buildLast30Dates() {
  const dates = [];
  // Kết thúc tại hôm qua (today - 1)
  for (let i = 29; i >= 0; i--) {
    dates.push(
      dayjs()
        .subtract(i + 1, 'day')
        .format('YYYY-MM-DD')
    );
  }
  return dates;
}

export default function NightMinConsumptionList() {
  const { nightMinConsumption } = usePermission();
  const [data, setData] = useState([]);
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [loadingSync, setLoadingSync] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [search, setSearch] = useState('');
  const [flagSearch, setFlagSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    nhi_ngo: 0,
    binh_thuong: 0,
    chua_du_du_lieu: 0,
    total_all: 0
  });
  const [isOpen, setIsOpen] = useState(false);
  const [dataFilterAdvance, setDataFilterAdvance] = useState({});
  const [favoriteFilters, setFavoriteFilters] = useState([]);
  const [selectedFavoriteFilter, setSelectedFavoriteFilter] = useState(null);
  const [loadingFavoriteFilters, setLoadingFavoriteFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Cột các ngày được tính sẵn ở client (nhất quán với server dùng 30 ngày gần nhất)
  const dates = buildLast30Dates();

  const fetchFavoriteFilters = async () => {
    try {
      setLoadingFavoriteFilters(true);
      const res = await apiGetFavoriteFilters();
      const customerFilters = (res.data?.data || []).filter((f) => f.filter_type === 'customer');
      setFavoriteFilters(
        customerFilters.map((f) => ({
          label: `${f.filter_name} (${f.item_ids?.length || 0} KH)`,
          value: f.id,
          item_ids: f.item_ids
        }))
      );
    } catch (err) {
      console.error('Error fetching favorite filters:', err);
    } finally {
      setLoadingFavoriteFilters(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoadingFetch(true);
      const res = await apiGetNightMinConsumptionTracking({
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined,
        listCust: selectedFavoriteFilter
          ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter)?.item_ids?.join(',')
          : undefined,
        leak_status: activeTab !== 'all' ? activeTab : undefined,
        ...dataFilterAdvance
      });
      const LEAK_ORDER = { 'Nghi ngờ': 0, 'Chưa đủ dữ liệu': 1, 'Bình thường': 2 };
      const sorted = (res.data?.data ?? []).sort(
        (a, b) => (LEAK_ORDER[a.leak_status] ?? 2) - (LEAK_ORDER[b.leak_status] ?? 2)
      );
      setData(
        sorted.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total ?? 0);
      setStatusCounts({
        nhi_ngo: res.data?.meta?.nhi_ngo ?? 0,
        binh_thuong: res.data?.meta?.binh_thuong ?? 0,
        chua_du_du_lieu: res.data?.meta?.chua_du_du_lieu ?? 0,
        total_all: res.data?.meta?.total_all ?? 0
      });
    } catch (err) {
      showError({ message: 'Something went wrong!' });
      console.error(err);
    } finally {
      setLoadingFetch(false);
    }
  };

  const handleSyncButtonClick = async () => {
    try {
      setLoadingSync(true);
      await apiSyncTimeData();
      await apiClearNightMinConsumptionCache();
      fetchData();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSync(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoadingExport(true);
      const res = await apiExportNightMinConsumptionTracking({
        keyWord: search ?? undefined,
        listCust: selectedFavoriteFilter
          ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter)?.item_ids?.join(',')
          : undefined,
        leak_status: activeTab !== 'all' ? activeTab : undefined,
        ...dataFilterAdvance
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `theo_doi_tieu_thu_dem_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showError({ message: 'Xuất Excel thất bại!' });
      console.error(e);
    } finally {
      setLoadingExport(false);
    }
  };

  useEffect(() => {
    fetchFavoriteFilters();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, activeTab]); // eslint-disable-line

  // ─── Cột cố định thông tin khách hàng ───────────────────────────────────────
  const fixedColumns = [
    {
      title: 'DBĐD',
      dataIndex: 'code',
      fixed: 'left',
      width: 80,
      render: (text) => <span style={{ color: '#0051A9' }}>{text}</span>
    },
    {
      title: 'Tên khách hàng',
      dataIndex: 'name',
      fixed: 'left',
      width: 180,
      render: (text) => <span style={{ color: '#0051A9' }}>{text}</span>
    },
    { title: 'Địa chỉ', dataIndex: 'address', width: 200 },
    { title: 'Nhà máy', dataIndex: 'branch', width: 100 },
    { title: 'TQL', dataIndex: 'manage_group', width: 70 },
    { title: 'ĐHT', dataIndex: 'parent_device', width: 70 },
    { title: 'Mạng lưới', dataIndex: 'route', width: 80 },

    { title: 'Cỡ ĐH', dataIndex: 'size', width: 70 },
    {
      title: 'Thiết bị',
      dataIndex: 'serial_no',
      width: 130,
      render: (_, record) => (
        <span style={{ color: '#0051A9' }}>{`SN:${record.serial_no} / IMEI:${record.imei}`}</span>
      )
    }
  ];

  // ─── Cột 30 ngày ────────────────────────────────────────────────────────────
  const dayColumns = dates.map((date) => {
    const colKey = `night_min_${date.replace(/-/g, '_')}`;
    const label = dayjs(date).format('DD/MM');
    return {
      title: <Tooltip title={date}>{label}</Tooltip>,
      dataIndex: colKey,
      key: colKey,
      width: 80,
      align: 'right',
      render: (val) => formatCubicMeters(val)
    };
  });

  // ─── Cột tổng hợp & trạng thái ──────────────────────────────────────────────
  const summaryColumns = [
    {
      title: 'TT đêm nhỏ nhất (m3)',
      dataIndex: 'tt_dem_nho_nhat',
      key: 'tt_dem_nho_nhat',
      width: 130,
      fixed: 'right',
      render: (val) => (
        <span style={{ color: '#0051A9', fontSize: '17px', fontWeight: 'bold' }}>
          {formatCubicMeters(val)}
        </span>
      )
    },
    {
      title: 'Ngưỡng so sánh (m3)',
      dataIndex: 'nguong_so_sanh',
      key: 'nguong_so_sanh',
      width: 130,
      fixed: 'right',
      render: (val) => (
        <span style={{ color: '#0051A9', fontSize: '17px', fontWeight: 'bold' }}>
          {formatCubicMeters(val)}
        </span>
      )
    },
    {
      title: 'Trạng thái rò rỉ',
      dataIndex: 'leak_status',
      key: 'leak_status',
      fixed: 'right',
      width: 130,
      align: 'center',
      onCell: (record) => ({
        style: {
          backgroundColor:
            record.leak_status === 'Nghi ngờ'
              ? '#fff1f0'
              : record.leak_status === 'Bình thường'
              ? '#f6ffed'
              : undefined
        }
      }),
      render: (val) =>
        val === 'Nghi ngờ' ? (
          <Tag color="red">{val}</Tag>
        ) : val === 'Bình thường' ? (
          <Tag color="green">{val}</Tag>
        ) : (
          <Tag color="orange">{val ?? 'Chưa đủ dữ liệu'}</Tag>
        )
    }
  ];

  const columns = [
    ...fixedColumns,
    {
      title: 'Tiêu thụ đêm nhỏ nhất các ngày (m3)',
      children: dayColumns
    },
    ...summaryColumns
  ];

  const tabItems = [
    { key: 'all', label: `Tất cả (${statusCounts.total_all})` },
    {
      key: 'Nghi ngờ',
      label: (
        <span>
          Nghi ngờ{' '}
          <Tag color="red" style={{ marginLeft: 4 }}>
            {statusCounts.nhi_ngo}
          </Tag>
        </span>
      )
    },
    {
      key: 'Bình thường',
      label: (
        <span>
          Bình thường{' '}
          <Tag color="green" style={{ marginLeft: 4 }}>
            {statusCounts.binh_thuong}
          </Tag>
        </span>
      )
    },
    {
      key: 'Chưa đủ dữ liệu',
      label: (
        <span>
          Chưa đủ dữ liệu{' '}
          <Tag color="orange" style={{ marginLeft: 4 }}>
            {statusCounts.chua_du_du_lieu}
          </Tag>
        </span>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Typography.Title level={4} style={{ color: '#0051A9', margin: 0 }}>
            Theo dõi lưu lượng ban đêm
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            {nightMinConsumption.export && (
              <Button
                icon={<DownloadOutlined />}
                loading={loadingExport}
                onClick={handleExport}
                style={{ height: 36 }}>
                Xuất Excel
              </Button>
            )}
            {nightMinConsumption.sync && (
              <Button
                icon={<SyncOutlined color="#0051A9" />}
                loading={loadingSync}
                onClick={handleSyncButtonClick}
                style={{ height: 36, color: '#0051A9' }}>
                Làm mới
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Card bodyStyle={{ padding: 8 }}>
        <Row gutter={[8, 8]}>
          <Col md={1} xs={1}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setIsOpen(true)}
              style={{
                backgroundColor: '#F4F5F7',
                color: '#626D7C',
                border: 'none',
                width: '100%'
              }}
            />
          </Col>

          <Col md={5} xs={8}>
            <Select
              placeholder="Chọn Bộ lọc theo dõi..."
              value={selectedFavoriteFilter}
              onChange={(value) => {
                setSelectedFavoriteFilter(value);
                setCurrentPage(1);
              }}
              loading={loadingFavoriteFilters}
              allowClear
              options={favoriteFilters}
              style={{ width: '100%', height: 36 }}
            />
          </Col>

          <Col md={5} xs={8}>
            <Input
              style={{ height: 36, width: '100%' }}
              placeholder="Tìm kiếm theo mã / tên KH..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => {
                setCurrentPage(1);
                setFlagSearch((f) => !f);
              }}
            />
          </Col>

          <Col md={5} xs={8}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                style={{ height: 36, backgroundColor: '#0051A9', borderColor: '#0051A9' }}
                onClick={() => {
                  setCurrentPage(1);
                  setFlagSearch((f) => !f);
                }}>
                Tìm kiếm
              </Button>
              <Button
                icon={<ClearOutlined />}
                style={{ height: 36 }}
                onClick={() => {
                  setSearch('');
                  setSelectedFavoriteFilter(null);
                  setDataFilterAdvance({});
                  setCurrentPage(1);
                  setFlagSearch((f) => !f);
                }}>
                Xoá lọc
              </Button>
            </Space>
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setCurrentPage(1);
          }}
          items={tabItems}
          style={{ marginBottom: 8 }}
        />
      </Card>

      <Table
        bordered
        loading={loadingFetch ? { indicator: <Loading /> } : false}
        dataSource={data}
        columns={columns}
        rowKey="stt"
        pagination={{
          current: currentPage,
          pageSize,
          total: totalItems,
          showSizeChanger: true,
          onChange: (page) => setCurrentPage(page),
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
        }}
        scroll={{ x: 'max-content', y: 450 }}
        style={{ borderRadius: 0 }}
        onRow={(record) => ({
          onClick: () => setSelectedRecord((prev) => (prev?.stt === record.stt ? null : record)),
          style: {
            cursor: 'pointer',
            background: selectedRecord?.stt === record.stt ? '#e6f0ff' : undefined
          }
        })}
      />

      {/* ── Biểu đồ cột tiêu thụ đêm nhỏ nhất ── */}
      {selectedRecord ? (
        <div style={{ marginTop: 20 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8, color: '#0051A9' }}>
            Biểu đồ tiêu thụ đêm nhỏ nhất: [{selectedRecord.code}] {selectedRecord.name}
          </Typography.Text>
          <Chart
            type="bar"
            height={260}
            xAxis={dates.map((d) => dayjs(d).format('DD/MM'))}
            series={[
              {
                name: 'Tiêu thụ đêm (m3)',
                data: dates.map((d) => {
                  const val = selectedRecord[`night_min_${d.replace(/-/g, '_')}`];
                  return toCubicMeters(val);
                })
              }
            ]}
            customOptions={{
              colors: ['#0051A9'],
              plotOptions: { bar: { columnWidth: '60%', borderRadius: 0 } },
              dataLabels: { enabled: false },
              yaxis: {
                title: { text: 'm3' },
                labels: { formatter: (v) => (v != null ? formatCurrency(v, 3) : '-') }
              },
              xaxis: { categories: dates.map((d) => dayjs(d).format('DD/MM')), tickAmount: 15 },
              tooltip: {
                y: {
                  formatter: (v) => (v != null ? `${formatCurrency(v, 3)} m3` : 'Không có dữ liệu')
                }
              },
              annotations: {
                yaxis:
                  isNumber(selectedRecord.nguong_so_sanh) && isFinite(selectedRecord.nguong_so_sanh)
                    ? [
                        {
                          y: selectedRecord.nguong_so_sanh / 1000,
                          borderColor: '#ff4d4f',
                          strokeDashArray: 4,
                          label: {
                            borderColor: '#ff4d4f',
                            style: { color: '#fff', background: '#ff4d4f', fontSize: '11px' },
                            text: `Ngưỡng: ${formatCurrency(
                              selectedRecord.nguong_so_sanh / 1000,
                              3
                            )} m3`
                          }
                        }
                      ]
                    : []
              }
            }}
          />
        </div>
      ) : (
        <div
          style={{
            marginTop: 16,
            textAlign: 'center',
            padding: '12px 0',
            color: '#aaa',
            fontSize: 13
          }}>
          Bấm vào một bản ghi để xem biểu đồ tiêu thụ đêm nhỏ nhất
        </div>
      )}

      <FilterAdvanceDrawer
        isOpen={isOpen}
        onDrawerClose={() => setIsOpen(false)}
        dataFilterAdvance={dataFilterAdvance}
        setDataFilterAdvance={setDataFilterAdvance}
        flagSearch={flagSearch}
        setFlagSearch={setFlagSearch}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
}
